import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import UserList from "../components/reusable/usersList";
import { fetchAdminStatus } from "../services/adminService";
import { fetchEntries } from "../services/dataService";
import { deleteEntry } from "../services/deleteEntry";
import Card from "../components/reusable/card.jsx";
import ToggleButtons from "../components/reusable/toggleButtons.jsx";
import DateSelector from "../components/reusable/DateSelector.jsx";
import LoadingScreen from "../components/reusable/loadingScreen";
import { motion, AnimatePresence } from "framer-motion";
import PageHeader from "../components/reusable/PageHeader.jsx";
import { useTranslation } from "react-i18next";

function ViewEntries(props) {
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState("morning");
  const [morningEntries, setMorningEntries] = useState([]);
  const [eveningEntries, setEveningEntries] = useState([]);
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [userId, setUserId] = useState("");
  const [admin, setAdmin] = useState(false);
  const [initial, setIsInitial] = useState(true);
  const [loading, setLoading] = useState(false);
  const [username, setUserName] = useState("");
  const [name, setName] = useState("");
  const location = useLocation();

  const getActiveItemFromPath = () => {
    if (location.pathname.includes("/morning")) return "morning";
    if (location.pathname.includes("/evening")) return "evening";
    return "morning";
  };

  useEffect(() => {
    if (location.state) {
      setUserId(location.state.id);
      setUserName(location.state.username);
      setName(location.state.name);
    }
  }, [location.state]);

  useEffect(() => {
    setActiveItem(getActiveItemFromPath());
  }, [location.pathname]);

  useEffect(() => {
    setAdmin(fetchAdminStatus());
  }, []);

  const updateDates = (start, end) => {
    setStartDate(moment(start).format("YYYY-MM-DD"));
    setEndDate(moment(end).format("YYYY-MM-DD"));
    setIsInitial(false);
  };

  const handleDelete = async (itemId, time) => {
    try {
      await deleteEntry(itemId, time);
      fetchData();
    } catch (error) {
      console.error("Error deleting entry:", error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const { morningEntries, eveningEntries } = await fetchEntries(startDate, endDate, userId, props.userId);
      setMorningEntries(morningEntries);
      setEveningEntries(eveningEntries);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initial) fetchData();
  }, [startDate, endDate, userId, initial]);

  const renderContent = (entries, time) => (
    <motion.div
      key={time}
      initial={{ opacity: 0, y: time === "morning" ? -15 : 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: time === "morning" ? 15 : -15 }}
      transition={{ duration: 0.2 }}
      className="w-full"
    >
      {entries && entries.length > 0 ? (
        <div className="pt-2">
          <Card data={entries} admin={admin} onDelete={handleDelete} time={time} role={props.role} userName={name} />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-white border border-dashed border-line p-4 rounded-full mb-3">
             <svg className="h-6 w-6 text-line" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-[14px] font-bold text-primary">{t("viewEntries.empty_list")}</p>
          <p className="text-[#6B7280] text-[12px] mt-1 max-w-[200px] leading-relaxed">
            No records found for this date and shift.
          </p>
        </div>
      )}
    </motion.div>
  );

  return (
    <div className="min-h-screen w-full bg-paper pb-20 font-sans text-primary">
      <main className="px-4 pt-4 max-w-lg mx-auto">
        <PageHeader title={t("viewEntries.title")} />

        {/* --- COMPACT FILTER BAR --- */}
        <div className="bg-white border border-line rounded-2xl shadow-sm p-4 mb-5 space-y-4">
          
          {/* Top Row: User Selection (Admin Only) */}
          {props.role === "admin" && (
            <div className="w-full">
              <UserList 
                label={t("viewEntries.labels.select_user")}
                onSelectUser={setUserId} 
                onUserChange={setName} 
                selectedUser={username} 
                required={false}
              />
            </div>
          )}

          {/* Bottom Row: Date Range & Shift */}
          <div className="flex gap-3">
            <div className="flex-1 min-w-0">
              <label className="block text-[9.5px] font-bold uppercase tracking-widest text-primary mb-1.5 ml-0.5">
                {t("viewEntries.labels.date_range")}
              </label>
              <DateSelector handleChange={updateDates} />
            </div>
            
            <div className="flex-1 min-w-0">
              <label className="block text-[9.5px] font-bold uppercase tracking-widest text-primary mb-1.5 ml-0.5">
                {t("viewEntries.labels.select_time")}
              </label>
              <ToggleButtons
                name1={t("viewEntries.toggles.morning")}
                name2={t("viewEntries.toggles.evening")}
                onToggle={(val) => {
                  if (val === t("viewEntries.toggles.morning")) setActiveItem("morning");
                  else setActiveItem("evening");
                }}
              />
            </div>
          </div>
        </div>

        {/* --- CONTENT AREA --- */}
        <div className="w-full min-h-[200px] relative">
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div key="loading" className="w-full py-12 flex items-center justify-center">
                <LoadingScreen />
              </motion.div>
            ) : activeItem === "morning" ? (
              renderContent(morningEntries, "morning")
            ) : (
              renderContent(eveningEntries, "evening")
            )}
          </AnimatePresence>
        </div>
        
      </main>
    </div>
  );
}

export default ViewEntries;